import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InsuranceTableComponent } from '../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../componentes/campos/campos.component';
import { CotizadoresFilter } from '../../interfaces/filters.interfaces';
import {
  QuotePrintRequest,
  QuotePrintResponse,
  QuoteRequest,
  QuoteResponse,
} from '../../interfaces/cotizadores.interface';
import { TableConfig } from '../../interfaces/table-config.interface';
import { AuthService } from '../../services/auth.service';
import { CotizadoresService } from '../../services/cotizadores.service';
import { ApiResponse } from '../../interfaces/common.interfaces';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar las cotizaciones de seguros
 * Permite consultar, visualizar y exportar información sobre las cotizaciones
 */
@Component({
  selector: 'app-cotizadores',
  standalone: true,
  imports: [
    CommonModule,
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
  ],
  templateUrl: './cotizadores.component.html',
  styleUrl: './cotizadores.component.css',
})
export class CotizadoresComponent {
  insuranceData: QuoteResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  title: string = '';
  cotizacion: string = '';
  fecha1?: Date;
  fecha2?: Date;
  // Configuración de la tabla
  tableConfig: TableConfig = {
    columns: [
      { key: 'quoteNumber', label: 'Numero de Cotización', sortable: true },
      { key: 'clientName', label: 'Asegurado', sortable: true },
      { key: 'lobDescription', label: 'Descripción', sortable: true },
      {
        key: 'beginVigencia',
        label: 'Fecha de Inicio',
        sortable: true,
        type: 'date',
      },
      {
        key: 'endVigencia',
        label: 'Fecha de Fin',
        sortable: true,
        type: 'date',
      },
      {
        key: 'insuredAmount',
        label: 'Monto Asegurado',
        sortable: true,
        type: 'currency',
      },
      { key: 'totalPremium', label: 'Prima', sortable: true, type: 'currency' },
      {
        key: 'quote',
        label: 'Cotización',
        type: 'quote',
      },
    ],
    showTotals: true,
    totalFields: ['totalPremium'],
  };
  tableConfigExport: TableConfig = {
    columns: [
      { key: 'quoteNumber', label: 'Numero de Cotización', sortable: true },
      { key: 'clientName', label: 'Asegurado', sortable: true },
      { key: 'lobDescription', label: 'Descripción', sortable: true },
      {
        key: 'beginVigencia',
        label: 'Fecha de Inicio',
        sortable: true,
        type: 'date',
      },
      {
        key: 'endVigencia',
        label: 'Fecha de Fin',
        sortable: true,
        type: 'date',
      },
      {
        key: 'insuredAmount',
        label: 'Monto Asegurado',
        sortable: true,
        type: 'currency',
      },
      { key: 'totalPremium', label: 'Prima', sortable: true, type: 'currency' },
    ],
    showTotals: true,
    totalFields: ['totalPremium'],
  };
  // Referencia al componente de la tabla
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  /**
   * Constructor del componente
   * @param cotizadoresService Servicio para obtener datos de las cotizaciones
   * @param authService Servicio de autenticación
   */
  constructor(
    private cotizadoresService: CotizadoresService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    this.title = 'Consulta de Cotizaciones';
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza una nueva consulta al servidor con los filtros actualizados
   * @param filter Objeto con los filtros seleccionados por el usuario
   */
  onFilterChange(filter: CotizadoresFilter): void {
    if (Object.keys(filter).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      return;
    }

    this.fecha1 = filter.dateFrom;
    this.fecha2 = filter.dateTo;

    const request: QuoteRequest = {
      ProducerNumber: this.authService.getProducerNumber() || 0,
      lob: filter.lobId,
      slob: filter.sLobId,
      dateFrom: filter.dateFrom || undefined,
      dateTo: filter.dateTo || undefined,
    };

    this.cotizadoresService.getQuotes(request).subscribe({
      next: (response: HttpResponse<ApiResponse<QuoteResponse[]>>) => {
        if (response.status === 200 && response.body?.data) {
          this.insuranceData = response.body.data.map((item) => ({
            ...item,
            actions: 'print',
          }));
          this.mensajeNoData =
            response.body.data.length === 0
              ? 'No hay datos disponibles para mostrar'
              : '';
        }

        if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener las cotizaciones:', error);
        this.insuranceData = [];
        if (error.status === 404) {
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        } else {
          this.mensajeNoData =
            'No se han podido obtener los datos de las cotizaciones';
        }
      },
    });
  }

  /**
   * Maneja la exportación de datos en diferentes formatos
   * @param formato Formato de exportación deseado
   */
  onExportar(formato: string): void {
    if (this.tablaComponent) {
      const headerInfo = {
        titulo: 'Cotizaciones',
        lob: this.getLobName(),
        slob: this.getSlobName(),
        fecha1: this.fecha1!,
        fecha2: this.fecha2,
        tableConfig: this.tableConfig,
      };


         //Evento para trackear las exportaciones de archivos en Google Analytics
      this.gAnalytics.trackEvent('cotizaciones_exportar',{
        page:'/cotizaciones',
        format:formato
      })

      this.tablaComponent.exportarDatos(formato, headerInfo);
    }
  }

  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo (LOB) seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  public getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo (SLOB) seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  public getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }

  /**
   * Maneja la impresión de una cotización específica
   * @param item Cotización a imprimir
   */
  onPrintQuote(item: any) {
    const request: QuotePrintRequest = {
      process: 1,
      quoteNumber: item.quoteNumber,
      slob: item.slob,
      effectiveDate: this.formatDate(item.beginVigencia),
      cuser: item.user,
    };

    this.cotizadoresService.getQuotePrint(request).subscribe({
      next: (response: ApiResponse<QuotePrintResponse>) => {
        this.cotizacion = response.data.url;
        window.open(this.cotizacion);
      },
      error: (error) => {
        console.error('Error al obtener la cotización para imprimir:', error);
      },
    });
  }

  /**
   * Formatea una fecha al formato dd-mm-yyyy
   * @param date Fecha a formatear
   * @returns Fecha formateada como string
   */
  formatDate(date: string): string {
    if (!date) return '';

    const [fechaParte] = date.split('T');
    const [año, mes, dia] = fechaParte.split('-');

    return `${dia}-${mes}-${año}`;
  }
}
